<script>
  import { createEventDispatcher } from 'svelte';
  import { FontAwesomeIcon } from '@fortawesome/svelte-fontawesome';
  import { faXmark, faSearch, faCircleXmark } from '@fortawesome/pro-light-svg-icons';
  
  export let showModal = false;
  export let currentStudyId = null;
  export let title = "Select a Study";
  
  const dispatch = createEventDispatcher();
  // Using local API endpoints instead of direct API access
  const API_BASE_URL = '/api/proxy';
  
  let studies = [];
  let filteredStudies = [];
  let loading = false;
  let searchTerm = '';
  let currentPage = 1;
  let itemsPerPage = 10;
  
  $: if (showModal) {
    fetchStudies();
    currentPage = 1;
  }
  
  $: {
    if (searchTerm && studies.length > 0) {
      const term = searchTerm.toLowerCase();
      filteredStudies = studies.filter(
        study => study.studyName.toLowerCase().includes(term) || 
                 study.protocolNumber?.toLowerCase().includes(term)
      );
      currentPage = 1;
    } else {
      filteredStudies = [...studies];
    }
  }
  
  $: paginatedStudies = filteredStudies.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );
  
  $: totalPages = Math.ceil(filteredStudies.length / itemsPerPage);
  
  function goToPage(page) {
    if (page >= 1 && page <= totalPages) {
      currentPage = page;
    }
  }
  
  async function fetchStudies() {
    loading = true;
    try {
      const res = await fetch(`${API_BASE_URL}/studies?pageSize=999`);
      
      if (!res.ok) {
        throw new Error('Failed to fetch studies');
      }
      
      const response = await res.json();
      studies = Array.isArray(response.data) 
        ? response.data.filter(study => study._id !== currentStudyId) 
        : [];
      filteredStudies = [...studies];
    } catch (err) {
      console.error('Error fetching studies:', err);
      studies = [];
      filteredStudies = [];
    } finally {
      loading = false;
    }
  }
  
  function handleStudyClick(studyId) {
    dispatch('studySelected', studyId);
  }
  
  function handleClose() {
    dispatch('close');
  }

  function handleClearSearch() {
    searchTerm = '';
  }
</script>

{#if showModal}
  <div class="fixed inset-0 z-50 flex items-center justify-center backdrop-blur-sm">
    <!-- svelte-ignore a11y-click-events-have-key-events -->
    <!-- svelte-ignore a11y-no-static-element-interactions -->
    <div class="modal-overlay absolute inset-0 bg-slate-800 opacity-75" on:click={handleClose}></div>
    
    <div class="relative bg-white dark:bg-gray-900 w-full max-w-3xl max-h-[75vh] rounded shadow-lg flex flex-col">
      <!-- Header -->
      <div class="flex justify-between items-center px-4 py-3 border-b border-slate-200 dark:border-slate-700">
        <h2 class="text-base font-semibold text-slate-900 dark:text-white">{title}</h2>
        <button class="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200" on:click={handleClose}>
          <FontAwesomeIcon icon={faXmark} />
        </button>
      </div>
      
      <!-- Search -->
      <div class="px-4 py-2 border-b border-slate-200 dark:border-slate-700">
        <div class="relative">
          <div class="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
            <FontAwesomeIcon icon={faSearch} class="text-slate-400" />
          </div>
          <input
            bind:value={searchTerm}
            type="text"
            class="bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-100 text-xs border border-gray-300 dark:border-gray-600 rounded-full focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 block w-full pl-10 px-3 py-1.5 pr-8"
            placeholder="Search"
          />
          {#if searchTerm}
            <button
              on:click={handleClearSearch}
              class="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
              title="Clear search"
            >
              <FontAwesomeIcon icon={faCircleXmark} />
            </button>
          {/if}
        </div>
      </div>
      
      <!-- Study List -->
      <div class="flex-grow overflow-y-auto p-3">
        {#if loading}
          <div class="flex justify-center items-center h-24">
            <div class="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-blue-500"></div>
          </div>
        {:else if filteredStudies.length === 0}
          <div class="text-center text-slate-400 py-4">
            {searchTerm ? 'No studies match your search' : 'No other studies found'}
          </div>
        {:else}
          <div class="grid grid-cols-1 gap-2">
            {#each paginatedStudies as study}
              <button
                class="text-left p-2 border border-slate-200 dark:border-slate-700 rounded hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
                on:click={() => handleStudyClick(study._id)}
              >
                <div class="font-medium text-slate-900 dark:text-white text-sm">{study.studyName}</div>
                {#if study.protocolNumber}
                  <div class="text-xs text-slate-500 dark:text-slate-400">Protocol: {study.protocolNumber}</div>
                {/if}
                {#if study.studyDescription}
                  <div class="text-xs text-slate-500 dark:text-slate-400 line-clamp-1">{study.studyDescription}</div>
                {/if}
              </button>
            {/each}
          </div>
          
          <!-- Pagination -->
          {#if totalPages > 1}
            <div class="flex items-center justify-center mt-4 space-x-1">
              <button 
                class="px-2 py-1 rounded text-xs {currentPage === 1 ? 'text-slate-400 cursor-not-allowed' : 'text-blue-600 hover:bg-slate-100 dark:hover:bg-slate-700'}"
                on:click={() => goToPage(currentPage - 1)}
                disabled={currentPage === 1}
              >
                &laquo; Prev
              </button>
              
              <div class="text-xs text-slate-600 dark:text-slate-300">
                Page {currentPage} of {totalPages}
              </div>
              
              <button 
                class="px-2 py-1 rounded text-xs {currentPage === totalPages ? 'text-slate-400 cursor-not-allowed' : 'text-blue-600 hover:bg-slate-100 dark:hover:bg-slate-700'}"
                on:click={() => goToPage(currentPage + 1)}
                disabled={currentPage === totalPages}
              >
                Next &raquo;
              </button>
            </div>
          {/if}
        {/if}
      </div>
      
      <!-- Footer -->
      <div class="px-4 py-2 border-t border-slate-200 dark:border-slate-700 text-right">
        <button
          class="px-3 py-1 text-sm bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-200 rounded hover:bg-slate-300 dark:hover:bg-slate-600 transition-colors"
          on:click={handleClose}
        >
          Cancel
        </button>
      </div>
    </div>
  </div>
{/if} 